import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";

/**
 * Giant embedded media screen for the wall behind the throne. Drop any
 * `.mp4` / `.webm` / `.gif` into `client/public/videos/` and point `src` at it.
 *
 * Video files use THREE.VideoTexture (hardware-decoded, cheap). Animated GIFs
 * are drawn off-screen and copied into a CanvasTexture each frame — the
 * `<img>` must stay attached to the DOM so the browser keeps advancing GIF
 * frames; we park it off-screen at -99999px.
 */

interface ThroneVideoScreenProps {
  src: string;
  position: [number, number, number];
  rotation?: [number, number, number];
  width?: number;
  height?: number;
  /** Slight self-illumination so the screen reads as "powered on". */
  emissiveIntensity?: number;
}

export function ThroneVideoScreen({
  src,
  position,
  rotation = [0, 0, 0],
  width = 7,
  height = 5,
  emissiveIntensity = 0.9,
}: ThroneVideoScreenProps): JSX.Element {
  const isGif = useMemo(() => /\.gif(\?|#|$)/i.test(src), [src]);

  return isGif ? (
    <GifScreen
      src={src}
      position={position}
      rotation={rotation}
      width={width}
      height={height}
      emissiveIntensity={emissiveIntensity}
    />
  ) : (
    <VideoScreen
      src={src}
      position={position}
      rotation={rotation}
      width={width}
      height={height}
      emissiveIntensity={emissiveIntensity}
    />
  );
}

function VideoScreen({
  src,
  position,
  rotation,
  width,
  height,
  emissiveIntensity,
}: Required<ThroneVideoScreenProps>): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Pair = (canvas, texture). Created only once the video reports real
  // dimensions, so the GPU allocates the texture at the correct size on
  // the first upload — otherwise texSubImage2D writes exceed the texture's
  // allocated size and silently fail (leaving the screen black).
  const [pair, setPair] = useState<{
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    texture: THREE.CanvasTexture;
  } | null>(null);

  useEffect(() => {
    const v = document.createElement("video");
    videoRef.current = v;
    // crossOrigin MUST be set before src to avoid tainting the canvas.
    v.crossOrigin = "anonymous";
    v.loop = true;
    v.muted = true;
    v.autoplay = true;
    v.playsInline = true;
    v.preload = "auto";
    v.style.position = "fixed";
    v.style.left = "-99999px";
    v.style.top = "-99999px";
    v.style.width = "1px";
    v.style.height = "1px";
    v.style.opacity = "0";
    v.style.pointerEvents = "none";

    v.addEventListener("loadeddata", () => {
      const w = v.videoWidth || 512;
      const h = v.videoHeight || 512;
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      if (!ctx) return;
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      setPair({ canvas: c, ctx, texture: tex });
      // eslint-disable-next-line no-console
      console.log("[ThroneVideoScreen] ready", src, w, h);
    });
    v.addEventListener("error", () => {
      // eslint-disable-next-line no-console
      console.warn("[ThroneVideoScreen] video error", src, v.error);
    });

    document.body.appendChild(v);
    v.src = src;

    const tryPlay = (): void => {
      v.play().catch(() => {
        const retry = (): void => {
          v.play().catch(() => {});
          window.removeEventListener("pointerdown", retry);
          window.removeEventListener("keydown", retry);
        };
        window.addEventListener("pointerdown", retry, { once: true });
        window.addEventListener("keydown", retry, { once: true });
      });
    };
    tryPlay();

    return () => {
      videoRef.current = null;
      v.pause();
      v.removeAttribute("src");
      v.load();
      v.remove();
    };
  }, [src]);

  // Watchdog — keep retrying play() if the video stalls.
  useEffect(() => {
    const t = window.setInterval(() => {
      const v = videoRef.current;
      if (!v) return;
      if (v.paused && v.readyState >= 2) v.play().catch(() => {});
    }, 2000);
    return () => window.clearInterval(t);
  }, []);

  // Per-frame blit: copy the current video frame into the canvas, then mark
  // the CanvasTexture dirty so the renderer reuploads.
  const drawErrLoggedRef = useRef(false);
  const lastFrameTimeRef = useRef(-1);
  useFrame(() => {
    if (!pair) return;
    const v = videoRef.current;
    if (!v || v.readyState < 2) return;
    // The render loop runs at 60fps but the video decodes at ~24-30fps. Only
    // re-blit + re-upload when the video has actually advanced to a new frame;
    // uploading the same frame twice wastes ~half the GPU texture bandwidth and
    // the accumulated pressure is what eventually loses the WebGL context in
    // long sessions (the "glitch after a while" symptom).
    if (v.currentTime === lastFrameTimeRef.current) return;
    lastFrameTimeRef.current = v.currentTime;
    try {
      pair.ctx.drawImage(v, 0, 0, pair.canvas.width, pair.canvas.height);
      pair.texture.needsUpdate = true;
    } catch (err) {
      if (!drawErrLoggedRef.current) {
        drawErrLoggedRef.current = true;
        // eslint-disable-next-line no-console
        console.warn("[ThroneVideoScreen] drawImage failed", src, err);
      }
    }
  });

  useEffect(() => {
    return () => {
      if (pair) pair.texture.dispose();
    };
  }, [pair]);

  if (!pair) {
    return (
      <group position={position} rotation={rotation}>
        <mesh>
          <planeGeometry args={[width, height]} />
          <meshBasicMaterial color="#0a0a0c" />
        </mesh>
      </group>
    );
  }

  return (
    <ScreenMesh
      texture={pair.texture}
      position={position}
      rotation={rotation}
      width={width}
      height={height}
      emissiveIntensity={emissiveIntensity}
    />
  );
}

function GifScreen({
  src,
  position,
  rotation,
  width,
  height,
  emissiveIntensity,
}: Required<ThroneVideoScreenProps>): JSX.Element {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const readyRef = useRef(false);

  const { canvas, texture } = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 512;
    c.height = 512;
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    return { canvas: c, texture: tex };
  }, []);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.style.position = "fixed";
    img.style.left = "-99999px";
    img.style.top = "-99999px";
    img.style.pointerEvents = "none";
    img.style.opacity = "0";
    img.style.width = "1px";
    img.style.height = "1px";
    img.onload = () => {
      canvas.width = img.naturalWidth || 512;
      canvas.height = img.naturalHeight || 512;
      ctxRef.current = canvas.getContext("2d");
      readyRef.current = true;
    };
    img.src = src;
    document.body.appendChild(img);
    imgRef.current = img;
    return () => {
      readyRef.current = false;
      ctxRef.current = null;
      imgRef.current = null;
      img.remove();
      texture.dispose();
    };
  }, [src, canvas, texture]);

  useFrame(() => {
    if (!readyRef.current) return;
    const img = imgRef.current;
    const ctx = ctxRef.current;
    if (!img || !ctx) return;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    texture.needsUpdate = true;
  });

  return (
    <ScreenMesh
      texture={texture}
      position={position}
      rotation={rotation}
      width={width}
      height={height}
      emissiveIntensity={emissiveIntensity}
    />
  );
}

interface ScreenMeshProps {
  texture: THREE.Texture;
  position: [number, number, number];
  rotation: [number, number, number];
  width: number;
  height: number;
  emissiveIntensity: number;
}
function ScreenMesh({
  texture,
  position,
  rotation,
  width,
  height,
  emissiveIntensity,
}: ScreenMeshProps): JSX.Element {
  void emissiveIntensity;
  const bezel = 0.18;
  const bezelDepth = 0.08;
  return (
    <group position={position} rotation={rotation}>
      {/* Bezel behind the screen — its back face sits at z = -bezelDepth,
          its front face at z = 0. The video plane lives at z = +0.01, just
          in front of the bezel's front face (no z-fight at any reasonable
          camera distance). */}
      <mesh position={[0, 0, -bezelDepth / 2]}>
        <boxGeometry args={[width + bezel * 2, height + bezel * 2, bezelDepth]} />
        <meshStandardMaterial color="#0a0a0c" roughness={0.4} metalness={0.6} />
      </mesh>
      <mesh position={[0, 0, 0.01]}>
        <planeGeometry args={[width, height]} />
        {/* meshBasicMaterial is unlit — no gamma/tonemap interaction, no
            emissive/lighting gymnastics. The video sRGB texture displays
            its own pixels at full brightness, which is what a screen does. */}
        <meshBasicMaterial
          map={texture}
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}
